import React from "react";
import Img from "gatsby-image";
import { useStaticQuery, graphql, Link } from "gatsby";
import dayjs from "dayjs";
import { PATH } from "~/constant/path";

import { breakPoints } from "~/utils/variables";
import { colors } from "~/utils/colors";
import {
  collectContentfulEventSlots,
  filterUpcomingContentfulEventSlots,
  formatContentfulEventSlotLines,
  getContentfulEventLastEndedAtRaw,
} from "~/utils/contentfulEventSlots";

export const InsightSidebar: React.FC = () => {
  const { events, articles } = useStaticQuery(graphql`
    query {
      events: allContentfulEvent(sort: { fields: date, order: DESC }) {
        edges {
          node {
            endedAt
            date
            secondDate
            secondEndedAt
            thirdDate
            thirdEndedAt
            title
            receptionStatus
            hasArchive
            contentful_id
            ogpImage {
              fluid(maxWidth: 1200, maxHeight: 630, resizingBehavior: FILL) {
                ...GatsbyContentfulFluid
              }
            }
          }
        }
      }
      articles: allContentfulInsightArticle(
        filter: { ranking: { lt: 11 } }
        sort: { order: ASC, fields: ranking }
        limit: 10
      ) {
        edges {
          node {
            contentful_id
            path
            id
            title
            cover {
              fixed(width: 85, height: 60) {
                ...GatsbyContentfulFixed
              }
            }
          }
        }
      }
    }
  `);
  const now = dayjs();
  const eventNodes = events.edges.map((edge) => edge.node);
  const openEvents = eventNodes.filter((event) => {
    // アーカイブ配信は除外
    if (event.hasArchive) {
      return false;
    }
    // 受付中で、終了時刻が現在より後であるもの
    const lastEnd = getContentfulEventLastEndedAtRaw({
      endedAt: event.endedAt,
      secondEndedAt: event.secondEndedAt,
      thirdEndedAt: event.thirdEndedAt,
    });
    return event.receptionStatus === true && lastEnd && now.isBefore(dayjs(lastEnd));
  });
  // 受付中がなければアーカイブ配信を表示
  const showingArchive = openEvents.length === 0;
  const activeEvents = showingArchive ? eventNodes.filter((event) => event.hasArchive) : openEvents;
  const eventsSectionTitle = showingArchive ? "アーカイブ配信中のセミナー" : "申込受付中のセミナー";

  return (
    <>
      <style jsx>{`
        .eventsContainer {
          margin-top: 30px;
        }
        .eventItem {
          margin-bottom: 30px;
        }
        .eventItem:last-child {
          margin-bottom: 0;
        }
        .eventImg {
          position: relative;
          box-shadow: none;
          border-radius: 8px;
          overflow: hidden;
          transition: box-shadow 0.3s ease, transform 0.3s ease;
        }
        .eventImg :global(a) {
          display: block;
        }
        .eventImg :global(.gatsby-image-wrapper),
        .eventImg :global(img) {
          border-radius: 8px;
        }
        .eventImg:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }
        .eventTitle {
          margin-top: 15px;
          color: ${colors.defaultText};
          font-size: 1.4rem;
          font-weight: 500;
          line-height: 1.6;
        }
        .eventTitle :global(a) {
          color: ${colors.defaultText};
        }
        .eventTitle :global(a:hover) {
          color: ${colors.orange};
        }
        .eventDate {
          margin-top: 8px;
          color: ${colors.littleBrightGray};
          font-size: 1.2rem;
          letter-spacing: 0.05rem;
        }
        .archiveLabel {
          color: ${colors.gray2};
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.02em;
          line-height: 1.4;
          margin: 4px 0 0;
        }
        .ranking {
          margin-top: 30px;
        }
        .rankingTitle {
          font-size: 2rem;
          font-weight: bold;
        }
        .rankings {
          list-style: none;
          margin: 30px 0 0;
        }
        .rankingItem {
          position: relative;
        }
        .rankingItem + .rankingItem {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px dotted #ccc;
        }
        .articleContainer {
          display: flex;
          align-items: flex-start;
        }
        .articleImg {
          width: 85px;
          position: relative;
          border-radius: 8px;
          overflow: hidden;
        }
        .articleImg :global(.gatsby-image-wrapper),
        .articleImg :global(img) {
          border-radius: 8px;
        }
        .articleTitle {
          width: calc(100% - 85px);
          padding-left: 15px;
          font-size: 1.3rem;
          font-weight: 500;
          line-height: 20px;
        }
        .articleTitle :global(a) {
          color: ${colors.defaultText};
        }
        .articleTitle :global(a:hover) {
          color: ${colors.orange};
        }
        .rankingNumber {
          border-radius: 24px;
          width: 24px;
          height: 24px;
          line-height: 24px;
          background-color: #333;
          left: 5px;
          top: 5px;
          color: ${colors.white};
          font-size: 1.5rem;
          font-weight: bold;
          text-align: center;
          position: absolute;
          z-index: 1;
        }
        .rankingNumber1 {
          background-color: #eac818;
        }
        .rankingNumber2 {
          background-color: #b5b5b5;
        }
        .rankingNumber3 {
          background-color: #c16e5b;
        }
        .wrapper {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        @media (${breakPoints.sp}) {
          .wrapper {
            padding: 0 30px;
          }
        }
      `}</style>
      <div className="wrapper">
        {activeEvents.length > 0 && (
          <>
            <h2 className="rankingTitle">{eventsSectionTitle}</h2>
            <div className="eventsContainer">
              {activeEvents.map((event) => (
                <div key={event.contentful_id} className="eventItem">
                  <div className="eventImg">
                    <Link to={`${PATH.EVENTS_SEMINAR.to}/${event.contentful_id}`}>
                      <Img fluid={event.ogpImage?.fluid} />
                    </Link>
                  </div>
                  {event.title && (
                    <h3 className="eventTitle">
                      <Link to={`${PATH.EVENTS_SEMINAR.to}/${event.contentful_id}`}>{event.title}</Link>
                    </h3>
                  )}
                  {showingArchive && <div className="archiveLabel">アーカイブ配信中</div>}
                  {formatContentfulEventSlotLines(
                    filterUpcomingContentfulEventSlots(
                      collectContentfulEventSlots({
                        date: event.date,
                        endedAt: event.endedAt,
                        secondDate: event.secondDate,
                        secondEndedAt: event.secondEndedAt,
                        thirdDate: event.thirdDate,
                        thirdEndedAt: event.thirdEndedAt,
                      }),
                      now
                    )
                  ).map((line, i) => (
                    <div key={`insight-event-${event.contentful_id}-${i}`} className="eventDate">
                      {line}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
        <div className="ranking">
          <h2 className="rankingTitle">ランキング</h2>
          <ul className="rankings">
            {articles.edges.map((edge, index) => {
              const { contentful_id, path, title } = edge.node;
              const pagePath = `${path || `${PATH.INSIGHT.to}/${contentful_id}`}`;

              return (
                <li key={`InsightSidebar_articles_${edge.node.contentful_id}`} className="rankingItem">
                  <div className="articleContainer">
                    <div className={`articleImg`}>
                      <div className={`rankingNumber rankingNumber${index + 1}`}>{index + 1}</div>
                      <Link to={pagePath}>
                        <Img fixed={edge.node.cover?.fixed} imgStyle={{ objectFit: "cover", objectPosition: "50% 50%" }} />
                      </Link>
                    </div>
                    <h3 className="articleTitle">
                      <Link to={pagePath}>{title}</Link>
                    </h3>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};
